import React, { useEffect } from "react";

import { StarIcon, PlayIcon } from "@heroicons/react/24/solid";
import { StarIcon as StarIconOutline } from "@heroicons/react/24/outline";
import { getCurrencySymbol, useWatchlistPaginated } from "@moonscape/shared";
import clsx from "clsx";
import { Input } from "@/components/ui/input";
import { SkeletonButton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { CoinIcon } from "../CoinIcon";

const WatchlistRow = ({ wl, baseCurrency, mutateWatchlist, setCoinId, isSelected }) => {
  return (
    <div className={clsx(
      "px-4 py-3 flex w-full items-center justify-between gap-4 rounded-lg transition-all group",
      {
        "bg-primary/10 border-l-4 border-l-primary": isSelected,
        "hover:bg-accent/50 dark:hover:bg-accent/30": !isSelected,
      }
    )}>
      <div
        className="flex flex-1 items-center justify-between overflow-hidden cursor-pointer"
        onClick={() => setCoinId(wl.coingeckoId)}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <CoinIcon coingeckoId={wl.coingeckoId} className="w-8 h-8 flex-shrink-0" />
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-semibold text-base text-foreground truncate">{wl.name}</span>
            <span className="text-sm text-muted-foreground uppercase font-medium">{wl.symbol}</span>
          </div>
        </div>

        {wl.price ? (
          <div className="flex items-center gap-6 ml-4">
            <div className="text-base font-semibold text-foreground whitespace-nowrap">
              {`${getCurrencySymbol(baseCurrency)} ${
                wl.price[baseCurrency?.toLowerCase()]
              }`}
            </div>
            <div className="flex items-center gap-1.5">
              <PlayIcon
                className={clsx("w-3 h-3", {
                  "rotate-90 fill-red-500 dark:fill-red-400":
                    wl.price[`${baseCurrency?.toLowerCase()}_24h_change`] < 0,
                  "-rotate-90 fill-green-500 dark:fill-green-400":
                    wl.price[`${baseCurrency?.toLowerCase()}_24h_change`] > 0,
                  "rotate-0 fill-muted-foreground":
                    Math.abs(
                      wl.price[`${baseCurrency?.toLowerCase()}_24h_change`]
                    ) < 1e-3,
                })}
              />
              <span
                className={clsx("text-sm font-semibold whitespace-nowrap min-w-[60px] text-right", {
                  "text-red-500 dark:text-red-400":
                    wl.price[`${baseCurrency?.toLowerCase()}_24h_change`] < 0,
                  "text-green-500 dark:text-green-400":
                    wl.price[`${baseCurrency?.toLowerCase()}_24h_change`] > 0,
                  "text-muted-foreground":
                    Math.abs(
                      wl.price[`${baseCurrency?.toLowerCase()}_24h_change`]
                    ) < 1e-3,
                })}
              >
                {`${(
                  wl.price[`${baseCurrency?.toLowerCase()}_24h_change`] || 0
                ).toFixed(2)}%`}
              </span>
            </div>
          </div>
        ) : (
          <SkeletonButton />
        )}
      </div>
      <button
        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-accent/80 dark:hover:bg-accent/60 transition-all flex-shrink-0 ml-2"
        onClick={(e) => {
          e.stopPropagation();
          mutateWatchlist({
            coingeckoId: wl.coingeckoId,
            isActive: !wl.isActive,
          });
        }}
      >
        {!wl.isActive ? (
          <StarIconOutline className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
        ) : (
          <StarIcon className="w-5 h-5 fill-yellow-400 dark:fill-yellow-300" />
        )}
      </button>
    </div>
  );
};
export const Watchlist = ({ baseCurrency, coingeckoId, setCoinId }) => {
  const {
    watchlist,
    error,
    isQueryLoading: isQueryLoadingWatchlist,
    searchQuery,
    setSearchQuery,
    mutateWatchlist,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useWatchlistPaginated();

  useEffect(() => {
    if (watchlist?.length && !coingeckoId) {
      setCoinId(watchlist[0]?.coingeckoId);
    }
  }, [watchlist]);
  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center gap-8 justify-between md:max-w-[600px] px-6 pt-6">
        <div className="text-lg font-semibold text-foreground">Watchlist</div>
        <div className="flex w-full">
          <Input
            value={searchQuery}
            placeholder="Find a symbol to add"
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-secondary-bg dark:bg-card"
          />
        </div>
      </div>
      <div className="p-2 overflow-x-hidden overflow-y-auto max-h-[320px] flex justify-between gap-2 md:gap-6 relative">
        <div className="flex flex-col w-full">
          <div className="w-full flex justify-between gap-2 md:gap-6 relative">
            {isQueryLoadingWatchlist || error ? (
              <div className="flex h-[320px] items-center justify-center w-full">
                <Spinner />
              </div>
            ) : (
              <div className="flex flex-col w-full">
                {(watchlist || [])?.map((wl) => {
                  return (
                    <WatchlistRow
                      wl={wl}
                      key={wl?.coingeckoId}
                      baseCurrency={baseCurrency}
                      setCoinId={setCoinId}
                      mutateWatchlist={mutateWatchlist}
                      isSelected={coingeckoId === wl.coingeckoId}
                    />
                  );
                })}

                {/* Load More Button - only show when searching */}
                {searchQuery && hasNextPage && (
                  <div className="px-2 py-4 flex justify-center">
                    <Button
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      variant="outline"
                    >
                      {isFetchingNextPage ? "Loading..." : "Load More"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
